import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';
import CurriculumDesc from '../../../components/qualifications/CurriculumDesc';

// ===== 専門資格ページ 新デザイン全面最新化（型正典: src/pages/qualifications/business-manner/page.tsx） =====
// 設計note（共通骨格・9ページ共通の設計言語）:
//   紺#1a2942 / 真鍮金#a38e65 / brass2 #8f7a56 / gold-signboard #d7b779 / cream #f8f6f0 / line #e2ddd2。
//   見出し=jba-display（Shippori Mincho B1）+ 金差し色span。eyebrow=Playfair uppercase。BTN-08 2状態。
//   共通セクション骨格（全異型・装飾ディバイダー/素hr/❦/紋章 不使用）:
//     Hero(全幅ビルボード) → I.資格概要(リード3段落+細罫データ行+特徴3枚) → II.カリキュラム(番号2カラム+画像)
//     → III.こんな方におすすめ(紺パネル+2カラム点検リスト) → IV.教材(番号カード+画像) → V.認定試験(紺罫定義リスト)
//     → VI.料金とお申込み(大字serif受講料+含まれるもの) → VII.FAQ(アコーディオン) → VIII.関連資格+締めCTA。
//   実文出典（創作禁止）: 現行working tree実文の逐語（scratchpad/qual-content.md §6 household-manager）。
//     数値・受講料・章立て・FAQは旧実装から改変せず継承。第9章の原文見出し「第90章：業者管理と調整」は誤植のため
//     位置ベース番号(09)で「業者管理と調整」としてレンダ（誤植は持ち込まない）。desc本文は逐語。
// household-manager = hero型A(左寄せ全幅) / 紺パネル主役=III（Standard）。

export default function HouseholdManagerButlerPage() {
  useEffect(() => {
    updateSEO({
      title: 'Household Manager Butler資格 | 邸宅管理のプロフェッショナル | 全日本執事協会',
      description: '全日本執事協会認定のHousehold Manager Butler資格。清掃技術、美術品の取り扱い、家具のメンテナンス、セキュリティ管理など、住環境を最適に保つための総合的なスキルを証明する専門資格。料金58,000円。',
      keywords: 'Household Manager Butler,邸宅管理,清掃技術,美術品,家具メンテナンス,セキュリティ管理,全日本執事協会',
      canonical: 'https://butler-association.jp/qualifications/household-manager'
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '資格制度', href: '/qualifications' },
    { label: 'Household Manager Butler' }
  ];

  // BTN-08（canon と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-6 sm:px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-normal text-center leading-[1.7] sm:whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { icon: 'ri-home-gear-line', title: '総合管理力', desc: '住環境全体を俯瞰する視点。清掃・調度・設備・防犯・庭園までを一つの視座でとらえ、住まい全体を最適に保ちます。' },
    { icon: 'ri-tools-line', title: '専門技術', desc: '素材別の適切なケア方法。家具・床材・繊維・美術品それぞれに応じた手入れの技術を、体系立てて習得します。' },
    { icon: 'ri-calendar-check-line', title: '計画性', desc: '長期的な維持管理の設計力。日々の管理から季節の配慮、業者調整まで、住環境を守る維持計画を立案します。' },
  ];

  const curriculum = [
    { no: '01', title: '執事と邸宅管理', desc: 'ハウスホールド・マネージャーとしての執事の歴史的役割、貴族の館における執事の責任範囲、住環境を守る執事の使命、トータル管理の視点を学びます。' },
    { no: '02', title: '住居の日常管理', desc: '日々の清掃と整理整頓の原則、換気と湿度管理の重要性、季節ごとの配慮事項、定期メンテナンスのスケジュール作成を習得します。' },
    { no: '03', title: '高度な清掃技術', desc: '素材別の適切な清掃方法、家具のケアとメンテナンス、フローリング・カーペットの管理、窓・照明の手入れ技術を学びます。' },
    { no: '04', title: '衣類の管理とケア', desc: '衣類の適切な保管方法、素材別の取り扱い注意点、クリーニングの知識と判断、季節の衣替えと長期保管を習得します。' },
    { no: '05', title: '調度品・美術品の管理', desc: '美術品の正しい取り扱い方法、アンティーク家具のケア技術、銀器・食器の手入れと保管、書籍の適切な保管方法を学びます。銀器の手入れで協会がまず勧めるのは、道具より先に手元を整えることです。素手の皮脂は指紋となって銀に残るため白手袋を着け、作業台には傷を防ぐ柔らかい布を広げてから銀器を取り上げます。磨く前の点検も欠かせません。曇りの出ている面、細かな傷、持ち手や飾りのぐらつきを一点ずつ目と指で確かめ、状態を把握してから磨きに入ります。\n\nシルバーポリッシュは、布に取って薄く伸ばすのが基本です。厚く塗るほど落ちるわけではなく、拭き残しがかえって曇りの原因になります。透かし彫りや継ぎ目など布の届かない箇所は毛先のやわらかいブラシで補い、力は入れずに布を往復させます。黒ずみが頑固な部分だけポリッシュを重ねて何度か繰り返します。一度で落とそうと強くこすると細かな傷が残るため、協会ではこの癖を最初に改めるよう勧めています。仕上げは乾いた別布で薬剤を残さず拭い、最後にもう一枚清潔な布で全体を撫でるように整えます。製品ごとに推奨される磨き方は違うので、初めて扱うポリッシュは目立たない箇所で試してから使います。\n\n銀という素材は、湿った空気、手の脂、食品や大気に含まれる硫黄と反応して、放っておくだけで黒ずんでいきます。だからこそ保管が手入れの半分です。使い終えたものは変色防止クロスで一点ずつ包み、湿気のこもらない場所へ収めます。金属同士が擦れると傷や変色を招くため、重ね置きは避けます。\n\n長く使わない銀器は、しまう前に一度磨いておくのが鉄則です。磨いてからクロスで包み、さらに布袋に収めて保管します。しまい込んだ後も月に一度は袋を開いて様子を見て、うっすらとした曇りの段階で手を入れるほうが、後から黒ずみを落とすよりはるかに楽です。お客様の目に触れる銀器は、来客の予定から逆算して前日までに点検を済ませておくことも協会では勧めています。' },
    { no: '06', title: '設備・機器の管理', desc: '家電製品のメンテナンス基礎、照明・空調の適切な管理、水回りの点検とトラブル予防、不具合の早期発見技術を習得します。' },
    { no: '07', title: 'セキュリティと防犯', desc: '防犯意識の基本原則、施錠管理の徹底、防犯設備の理解と活用、不審者への適切な対応方法を学びます。' },
    { no: '08', title: '庭園・外構の管理', desc: '庭の手入れの基本知識、植栽の管理とメンテナンス、季節の花の選定、外観の美観維持と定期点検を習得します。' },
    { no: '09', title: '業者管理と調整', desc: '専門業者の適切な選定方法、修繕・工事の立ち会い技術、定期点検のスケジュール管理、見積もりと予算管理の基本を学びます。' },
    { no: '10', title: '総合演習', desc: '邸宅管理計画の作成演習、トラブル対応シミュレーション、確認テストで学習内容を統合します。' },
  ];

  const recommend = [
    '邸宅管理のプロフェッショナルを目指す方', 'ハウスキーピング業界で働く方', 'マンション・ビル管理の仕事に就いている方',
    '家事代行サービスでスキルアップしたい方', '自宅を適切に管理したい方', '不動産管理・物件管理に携わる方',
    '高級住宅の管理職を目指す方', '執事資格と組み合わせて専門性を高めたい方',
  ];

  const materials = [
    { no: '1', title: 'テキスト教材（約100ページ）', desc: '邸宅管理の専門知識を詳しく解説した専用テキスト。' },
    { no: '2', title: 'オンライン動画レッスン', desc: '清掃技術や家具のケア方法を含む実践的な動画解説。視聴期間1年間。' },
    { no: '3', title: '素材別ケアガイド', desc: '家具・床材・繊維など素材別の手入れ方法をまとめた参考資料。' },
    { no: '4', title: '認定試験問題・解答用紙', desc: '筆記試験と管理計画作成課題を含む試験問題。' },
  ];

  const exam = [
    { label: '試験形式', value: '筆記試験（記述式・選択式）+ 管理計画作成課題' },
    { label: '合格基準', value: '80点以上' },
    { label: '試験期限', value: '申込から1年以内' },
    { label: '受験資格', value: '特になし（Associate Butler以上推奨）' },
    { label: '受験場所', value: '自宅（通信試験）' },
    { label: '再試験', value: '再試験料10,000円で何度でも可' },
  ];

  const included = [
    'テキスト教材（約100ページ）', 'オンライン動画レッスン（1年間視聴可能）', '素材別ケアガイド',
    '認定試験問題・解答用紙', '試験添削サービス', '合格時の認定証・認定カード', '送料',
  ];

  const related = [
    { href: '/qualifications/dining-coordinator', name: 'Dining Coordinator Butler資格', ja: '食事とテーブルコーディネートの専門家', icon: 'ri-restaurant-line' },
    { href: '/qualifications/event-planning', name: 'Event Planning Butler資格', ja: 'イベント企画運営のプロフェッショナル', icon: 'ri-calendar-event-line' },
    { href: '/qualifications', name: '専門資格一覧を見る', ja: '全ての専門資格をご覧いただけます', icon: 'ri-list-check' },
  ];

  const faqs = [
    { q: '家事未経験でも取得できますか？', a: 'はい、基礎から学べる内容です。日常の家事を体系的に学び直したい方にも適しています。' },
    { q: 'ハウスキーピング業界で働いています役立ちますか？', a: 'はい、現在の業務の質向上や知識の体系化に直結する実践的な内容です。' },
    { q: '自宅の管理に活用できますか？', a: 'はい、一般家庭でも応用できる実用的な知識が豊富に含まれています。' },
    { q: 'マンション管理の仕事に役立ちますか？', a: 'はい、共用部分の管理や住民対応にも応用できる知識を学びます。' },
    { q: '管理計画作成課題とは何ですか？', a: '与えられた住居の条件をもとに、年間の管理計画を作成する実技課題です。' },
    { q: '美術品の扱い方も学べますか？', a: 'はい、第5章で美術品やアンティーク家具の適切な取り扱い方法を詳しく学びます。' },
    { q: '防犯対策についても学べますか？', a: 'はい、第7章でセキュリティと防犯の基本を実践的に学びます。' },
    { q: '業者との調整方法は学べますか？', a: 'はい、第9章で専門業者の選定から見積もり比較まで学びます。' },
    { q: '清掃道具の選び方も学べますか？', a: 'はい、素材に適した清掃道具や洗剤の選び方も学習内容に含まれます。' },
    { q: '庭の手入れについても学べますか？', a: 'はい、第8章で庭園・外構の基本的な管理方法を学びます。' },
    { q: '家電製品のメンテナンスも学べますか？', a: 'はい、第6章で家電の基本的なメンテナンスと不具合の早期発見方法を学びます。' },
    { q: '衣類の保管方法も学べますか？', a: 'はい、第4章で素材別の適切な保管方法と季節の衣替えについて学びます。' },
    { q: 'カーペットとフローリングで手入れは違いますか？', a: 'はい、第3章で床材ごとの適切な清掃方法とメンテナンス技術を詳しく学びます。' },
    { q: '湿度管理の重要性も学べますか？', a: 'はい、第2章で換気と湿度管理が住環境に与える影響について学びます。' },
    { q: '高級住宅の管理職を目指していますが役立ちますか？', a: 'はい、富裕層の邸宅管理に必要な専門知識を体系的に習得できます。' },
  ];

  return (
    <Layout>
      {/* ===== Hero — 全幅ビルボード型A（画像+紺グラデ+左寄せ）。由来: about/overview ヒーロー ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-p-022-qual-household.webp"
          alt="邸宅管理の専門サービス。執事が住環境を整え、調度品や設備を管理する様子"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_46%,rgba(16,20,26,0.34)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Specialist Certification</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(30px,4vw,50px)] leading-[1.3] tracking-[0.04em] mb-3">Household Manager Butler</h1>
            <p className="font-['Noto_Serif_JP',serif] text-[#d7b779] text-base sm:text-lg tracking-[0.12em] mb-6">ハウスホールド・マネージャー・バトラー</p>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] max-w-[600px]">
              Household Manager Butler資格は、邸宅全体の管理と運営に関する専門知識を持つプロフェッショナルとしての資格です。清掃技術、美術品の取り扱い、家具のメンテナンス、セキュリティ管理など、住環境を最適に保つための総合的なスキルを証明します。
            </p>
          </div>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 space-y-16 sm:space-y-24">

          {/* ===== I. 資格概要（リード3段落+細罫データ行+特徴3枚）。由来: about/overview S1 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">I. About This Certification</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              邸宅のすべてを、<span className="text-[#a38e65]">総合的に管理する</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                Household Manager Butler資格は、邸宅全体の管理と運営に関する専門知識を持つプロフェッショナルとしての資格です。清掃技術、美術品の取り扱い、家具のメンテナンス、セキュリティ管理など、住環境を最適に保つための総合的なスキルを証明します。
              </p>
              <p>
                効率的な家事管理と長期的な住環境の維持計画を立案できる能力を習得。富裕層の邸宅管理や高級マンション管理職として、また一般家庭でも活用できる実践的な知識を身につけます。
              </p>
              <p>
                日々の清掃や設備の点検から、調度品・美術品の保全、庭園・外構の維持、専門業者との調整まで。住まいを構成するあらゆる要素を一人の視座で束ね、快適で美しい住環境を長く保つ視点を養う、邸宅全体を総合的に管理する専門家のための資格です。
              </p>
            </div>

            {/* 細罫データ行 */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受講料（税込）</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">58,000<span className="text-sm text-[#8f7a56] ml-1">円</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">学習期間目安</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2–4<span className="text-sm text-[#8f7a56] ml-1">ヶ月</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">カリキュラム</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">全10<span className="text-sm text-[#8f7a56] ml-1">章</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受験・資格</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">自宅受験・永久</dd>
              </div>
            </dl>

            {/* 特徴3枚（アイコン編集カード） */}
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
              {features.map((f) => (
                <div key={f.title} className="border-t-2 border-[#a38e65] bg-[#f8f6f0] px-6 py-7">
                  <i className={`${f.icon} text-3xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-2.5">{f.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 border border-[#a38e65]/40 bg-[#f8f6f0] px-5 py-4">
              <i className="ri-information-line text-[#a38e65] text-xl mt-0.5"></i>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                本資格は知識習得を目的とした認定制度であり、就職や収入を保証するものではありません。全日本執事協会が認定する民間資格です。
              </p>
            </div>
          </section>

          {/* ===== II. カリキュラム（番号2カラム編集ブロック+画像）。由来: about/overview S4 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">II. Curriculum</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学ぶ内容 — <span className="text-[#a38e65]">全10章</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              邸宅管理の専門知識を、10の分野で体系的に学びます。各章は実践的な内容で構成され、知識だけでなく実際の管理技術も習得できます。
            </p>
            <img
              src="/images/generated/jba-fs-165-qualifications_household-manager-v5.webp"
                alt="棚に整然と積んだタオルとラベンダー。邸宅管理で扱うリネン収納"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 leading-[1.5]">{c.title}</h3>
                    <CurriculumDesc text={c.desc} />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ===== III. こんな方におすすめ（紺パネル+2カラム点検リスト）= Standard主役セクション。由来: about/overview S3 翻案 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">III. Who It's For</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              こんな方におすすめです
            </h2>
            <div className="bg-[#1a2942] px-6 sm:px-10 lg:px-14 py-10 sm:py-12">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {recommend.map((r) => (
                  <div key={r} className="flex items-start gap-3 border-b border-white/10 pb-4">
                    <i className="ri-check-line text-[#d7b779] text-xl mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#ded8cc] leading-[1.8]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== IV. お届けする教材（番号カード+画像）。由来: about/overview S2 の罫線カード文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">IV. Course Materials</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              お届けする教材
            </h2>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {materials.map((m) => (
                  <div key={m.no} className="flex gap-4 border-t border-[#e2ddd2] pt-5">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl w-8 h-8 shrink-0 flex items-center justify-center border border-[#a38e65]/40 rounded-full">{m.no}</span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-base mb-1.5 leading-[1.5]">{m.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src="/images/generated/jba-fs-166-qualifications_household-manager-v7.webp"
                alt="リボンで束ねた白いリネン。邸宅管理における布類の保管"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* ===== V. 認定試験について（紺罫定義リスト）。由来: about/overview S2 の border-t-2 定義リスト文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">V. Examination</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              認定試験について
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {exam.map((row) => (
                <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[140px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.9] mt-5 max-w-3xl">
              試験は自宅で行う通信試験です。教材が届いたら、ご自身のペースで学習を進め、準備ができた段階で試験問題に取り組みます。解答用紙を返送いただくと、当協会が添削・採点し、80点以上で合格。認定証と認定カードを発行します。
            </p>
          </section>

          {/* ===== VI. 料金とお申込み（大字serif受講料+含まれるもの）。受講料は clamp 大字serif強調 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VI. Enrollment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金とお申込み
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/30 px-6 sm:px-10 lg:px-12 py-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-b border-[#e2ddd2] pb-8 mb-8">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] mb-2">受講料（税込・送料込）</p>
                  <div className="jba-display text-[#1a2942] leading-none">
                    <span className="font-['Playfair_Display',serif] text-[clamp(44px,7vw,64px)] font-semibold">58,000</span>
                    <span className="text-2xl sm:text-3xl ml-1">円</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center sm:text-right">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">学習期間目安</p>
                    <p className="jba-display text-[#1a2942] text-base">2〜4ヶ月</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">試験期限</p>
                    <p className="jba-display text-[#1a2942] text-base">1年以内</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">資格有効期限</p>
                    <p className="jba-display text-[#1a2942] text-base">永久</p>
                  </div>
                </div>
              </div>
              <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">料金に含まれるもの</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {included.map((it) => (
                  <div key={it} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#a38e65] text-lg mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{it}</span>
                  </div>
                ))}
              </div>
              <div className="mt-9">
                <a href="/contact" className={`${BTN08_ON_LIGHT} w-full sm:w-auto`}>Household Manager Butler資格に申し込む</a>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {['本資格は全日本執事協会が認定する民間資格です', '資格取得により就職・転職・収入を保証するものではありません', '知識習得と自己研鑽を目的としています'].map((n) => (
                <li key={n} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                  <i className="ri-information-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== VII. よくある質問（アコーディオン） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある質問
            </h2>
            <div className="border-t border-[#e2ddd2]">
              {faqs.map((f) => (
                <details key={f.q} className="group border-b border-[#e2ddd2]">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer py-5 list-none">
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-[17px] leading-[1.6]">{f.q}</span>
                    <i className="ri-add-line text-[#a38e65] text-xl shrink-0 transition-transform group-open:rotate-45"></i>
                  </summary>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] pb-5 pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ===== VIII. 関連資格 + 締めCTA（紺パネル）。由来: about/overview S6 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VIII. Related Certifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              あわせて学びたい専門資格
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              他の専門資格も組み合わせて取得することで、より幅広い専門性を証明できます。
            </p>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mb-12">
              {related.map((r) => (
                <a key={r.href} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] bg-white px-6 py-7 transition-colors">
                  <i className={`${r.icon} text-2xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-1.5 leading-[1.5]">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mb-4">{r.ja}</p>
                  <span className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] group-hover:text-[#a38e65] transition-colors">
                    詳しく見る<i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </a>
              ))}
            </div>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Household Manager Butler資格について、ご不明な点やお申込みのご相談はお気軽にお問い合わせください。専門資格の全体像は資格制度の一覧からもご覧いただけます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・申込</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度の一覧を見る</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
